import React, { useEffect, useMemo, useRef, useState } from 'react'
import { useParams, Link } from 'react-router-dom'
import { ArrowLeft, ChevronLeft, ChevronRight, Check, PanelLeftClose, PanelLeftOpen, Maximize, Minimize } from 'lucide-react'
import { API_CONFIG } from '../../../config/api.config'
import { lessonApi, resultApi } from '../api/lessonBuilderApi'
import LessonCardRenderer from '../components/LessonCardRenderer'
import QuestSection from '../components/LearningPath'
import { normalizeLesson, sectionKindMeta } from '../lib/sections'
import { isViewableTextBlock } from '../../../components/interactive/shared/contentBlocks'
import { LessonStatsService } from '../../../services/stats/LessonStatsService'
import { LessonDocProvider } from '../lib/lessonLive'
import { useAuth } from '../../../contexts/AuthContext'
import type { LessonDoc } from '../types/LessonDoc'

// Trình xem bài giảng kiểu e-learning: thanh tab các PHẦN bên trái + khu nội dung rộng,
// xem từng phần một, có thanh tiến độ và nút Phần trước/sau. Render bằng LessonCardRenderer
// (cùng đường render với builder → "xem trước = xuất bản").
const LessonViewerPage: React.FC = () => {
  const { slug } = useParams()
  const { user } = useAuth()
  const bypassLock = user?.role === 'teacher' || user?.role === 'admin'  // GV/Admin xem preview, không bị khoá ải
  const [lesson, setLesson] = useState<LessonDoc | null | undefined>(undefined)
  const [active, setActive] = useState(0)
  const [visited, setVisited] = useState<Set<number>>(new Set([0]))
  const [navOpen, setNavOpen] = useState(true)   // ẩn/hiện thanh tab các phần
  const [isFs, setIsFs] = useState(false)         // toàn màn hình (projector)
  const [, setStatsTick] = useState(0)            // nhịp làm tươi % tiến độ (service mutate ngoài React)

  // Đồng bộ trạng thái fullscreen với trình duyệt
  useEffect(() => {
    const onFs = () => setIsFs(!!document.fullscreenElement)
    document.addEventListener('fullscreenchange', onFs)
    return () => document.removeEventListener('fullscreenchange', onFs)
  }, [])

  const toggleFullscreen = () => {
    if (!document.fullscreenElement) {
      document.documentElement.requestFullscreen?.().catch(() => {})
      setNavOpen(false)  // vào toàn màn hình → ẩn tab để nội dung rộng tối đa
    } else {
      document.exitFullscreen?.().catch(() => {})
    }
  }

  useEffect(() => {
    if (slug) {
      lessonApi
        .getBySlug(slug)
        .then((c) => setLesson(c ? normalizeLesson(c) : null))
        .catch(() => setLesson(null))
    }
  }, [slug])

  // Chỉ điều hướng giữa các phần CÓ nội dung.
  const sections = useMemo(
    () => (lesson?.sections || []).filter((s) => s.cards.length > 0),
    [lesson]
  )

  // Kẹp chỉ số active trong khoảng hợp lệ + đánh dấu đã xem.
  useEffect(() => {
    if (active > sections.length - 1) setActive(Math.max(0, sections.length - 1))
  }, [sections.length, active])

  const goto = (i: number) => {
    const idx = Math.max(0, Math.min(sections.length - 1, i))
    setActive(idx)
    setVisited((prev) => new Set(prev).add(idx))
    window.scrollTo({ top: 0, behavior: 'smooth' })
  }

  // ── Ghi tiến độ học tập (/study/progress) để bài giảng e-learning vào thống kê GV ──
  const lastTickRef = useRef<number>(0)
  const lessonRef = useRef<LessonDoc | null>(null)
  const prevVisitedRef = useRef<number>(-1)
  const submittedRef = useRef<boolean>(false)
  const visitedRef = useRef<Set<number>>(new Set([0]))
  const totalRef = useRef<number>(0)
  const videoSeenRef = useRef<Set<number>>(new Set())
  useEffect(() => { lessonRef.current = lesson || null }, [lesson])
  useEffect(() => {
    visitedRef.current = visited
    totalRef.current = sections.length
  }, [visited, sections.length])

  // Mục tính tiến độ mỗi phần: KHỐI TEXT (đọc), CÂU HỎI tương tác (trả lời), VIDEO (xem).
  const GATE_TYPES = useMemo(() => new Set(['interactive_question', 'multiple_response_question', 'sequence_question']), [])
  const gateTotals = useMemo(
    () => sections.map((s) =>
      s.cards.reduce((n, c: any) =>
        n + (c.type === 'rich' ? (c.items || []).filter((it: any) => it && GATE_TYPES.has(it.type)).length : 0), 0)),
    [sections, GATE_TYPES]
  )
  // Tổng khối text đọc-được TRONG các thẻ rich (khớp nơi InteractiveContentRenderer ghi "đã xem khối").
  const textTotals = useMemo(
    () => sections.map((s) =>
      s.cards.reduce((n, c: any) =>
        n + (c.type === 'rich' ? (c.items || []).filter((it: any) => isViewableTextBlock(it)).length : 0), 0)),
    [sections]
  )
  const videoTotals = useMemo(() => sections.map((s) => s.cards.filter((c: any) => c.type === 'video').length), [sections])
  const sectionHasVideo = useMemo(() => videoTotals.map((n) => n > 0), [videoTotals])
  const gateTotalsRef = useRef<number[]>([])
  const textTotalsRef = useRef<number[]>([])
  const videoTotalsRef = useRef<number[]>([])
  const sectionTitlesRef = useRef<string[]>([])
  useEffect(() => {
    gateTotalsRef.current = gateTotals
    textTotalsRef.current = textTotals
    videoTotalsRef.current = videoTotals
    sectionTitlesRef.current = sections.map((s) => s.title || '')
  }, [gateTotals, textTotals, videoTotals, sections])

  // Tỉ lệ hoàn thành 1 phần = (khối text đã xem + câu hỏi đã trả lời + video đã xem) / tổng các mục đó.
  // Phần không có mục nào → coi xong khi đã xem. Đọc trực tiếp LessonStatsService (mutate ngoài React).
  const sectionRatioFrom = (i: number, b: any, viewed: boolean): number => {
    const tT = textTotalsRef.current[i] || 0
    const qT = gateTotalsRef.current[i] || 0
    const vT = videoTotalsRef.current[i] || 0
    const total = tT + qT + vT
    if (total === 0) return viewed ? 100 : 0
    const tD = Math.min(tT, b?.textViewed?.size || 0)
    const qD = Math.min(qT, (b?.correct || 0) + (b?.wrong || 0))
    const vD = vT > 0 && viewed ? vT : 0   // video tính "đã xem" khi đã vào phần
    return Math.round(((tD + qD + vD) / total) * 100)
  }

  const computeDone = (): { done: number; perSection: any[] } => {
    const l = lessonRef.current
    const key = l ? `bai-giang:${l.slug || l.id}` : ''
    const st = key ? LessonStatsService.getStats(key) : null
    const perSection: any[] = []
    let done = 0
    for (let i = 0; i < totalRef.current; i++) {
      const b = st?.sections?.[i]
      const viewed = visitedRef.current.has(i)
      const totalQ = gateTotalsRef.current[i] || 0
      const ratio = sectionRatioFrom(i, b, viewed)
      const isDone = ratio >= 100
      if (isDone) done++
      perSection.push({
        idx: i,
        title: sectionTitlesRef.current[i] || b?.title || `Phần ${i + 1}`,
        viewed,
        ratio,
        totalQuestions: totalQ,
        correct: b?.correct || 0,
        wrong: b?.wrong || 0,
        hints: b?.hints || 0,
        ai: b?.ai || 0,
        textViewed: Math.min(textTotalsRef.current[i] || 0, b?.textViewed?.size || 0),
        textTotal: textTotalsRef.current[i] || 0,
        done: isDone,
      })
    }
    return { done, perSection }
  }

  const authToken = () =>
    localStorage.getItem('jwt_token') || localStorage.getItem('auth_token') || localStorage.getItem('token') || ''

  // Gửi 1 lần: time_spent là DELTA (backend cộng dồn); progress/status ghi đè.
  const sendProgress = (deltaSec: number) => {
    const l = lessonRef.current
    const tk = authToken()
    if (!l || !tk) return
    // % hoàn thành theo phần ĐÃ HỌC (xem + trả lời hết câu hỏi chốt), không chỉ đã mở.
    const { done } = computeDone()
    const pct = totalRef.current ? Math.round((done / totalRef.current) * 100) : 0
    const status = pct >= 100 ? 'completed' : 'in_progress'
    const body = {
      lesson_id: `bai-giang:${l.slug || l.id}`,
      lesson_title: l.meta?.tieuDe || 'Bài giảng',
      progress: pct,
      status,
      time_spent: Math.max(0, Math.min(deltaSec, 600)), // chặn đếm thời gian tab bỏ ngỏ
      // % nội dung = số PHẦN ĐÃ HỌC / tổng phần (để /study/progress/stats tính đúng)
      meta_data: { completed_count: done, total_count: totalRef.current },
    }
    try {
      fetch(`${API_CONFIG.python.apiUrl}/study/progress`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${tk}` },
        body: JSON.stringify(body),
        keepalive: true,
      }).catch(() => {})
    } catch { /* noop */ }
  }

  const flushProgress = () => {
    if (!lastTickRef.current) return
    const delta = Math.floor((Date.now() - lastTickRef.current) / 1000)
    lastTickRef.current = Date.now()
    sendProgress(delta)
  }

  // Nộp kết quả câu hỏi chốt (tổng hợp từ LessonStatsService) về backend — 1 lần/phiên xem.
  const submitResults = () => {
    const l = lessonRef.current
    const tk = authToken()
    if (!l || !tk || submittedRef.current) return
    const key = `bai-giang:${l.slug || l.id}`
    const st = LessonStatsService.getStats(key)
    const answered = st ? st.correctAnswers + st.wrongAnswers : 0
    // Có tương tác câu hỏi HOẶC đã xem hết nội dung → mới gửi (tránh ghi rỗng).
    if (answered <= 0 && visitedRef.current.size < totalRef.current) return
    submittedRef.current = true
    const { done, perSection } = computeDone()
    const contentPercent = totalRef.current ? Math.round((done / totalRef.current) * 100) : 0
    resultApi.submit(l.id, {
      lesson_slug: l.slug,
      total: answered,
      correct: st?.correctAnswers || 0,
      score: st?.correctAnswers || 0,
      max_score: answered,
      // Chi tiết tương tác cho GV: đúng/sai, gợi ý, hỏi AI, video, breakdown TỪNG PHẦN.
      detail: {
        correct: st?.correctAnswers || 0,
        wrong: st?.wrongAnswers || 0,
        hintsUsed: st?.hintsUsed || 0,
        aiAsked: st?.aiQuestionsAsked || 0,
        videoWatched: st?.videoWatched || 0,
        sectionsViewed: visitedRef.current.size,
        sectionsDone: done,
        totalSections: totalRef.current,
        contentPercent,
        sections: perSection,
      },
    })
  }

  // Bắt đầu theo dõi khi bài giảng tải xong (gửi mốc in_progress) + flush khi rời/ẩn trang.
  useEffect(() => {
    if (!lesson || sections.length === 0 || !authToken()) return
    lastTickRef.current = Date.now()
    // Bắt đầu thu thống kê câu hỏi chốt cho bài này (questions tự ghi qua currentLessonId).
    const key = `bai-giang:${lesson.slug || lesson.id}`
    submittedRef.current = false
    videoSeenRef.current = new Set()
    LessonStatsService.resetStats(key)
    LessonStatsService.startLesson(key)
    LessonStatsService.setCurrentSection(0, sections[0]?.title)
    sendProgress(0)
    const onLeave = () => { flushProgress(); submitResults() }
    const onVis = () => { if (document.hidden) flushProgress() }
    window.addEventListener('beforeunload', onLeave)
    document.addEventListener('visibilitychange', onVis)
    return () => {
      window.removeEventListener('beforeunload', onLeave)
      document.removeEventListener('visibilitychange', onVis)
      onLeave()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lesson?.id, sections.length])

  // Mỗi lần xem thêm 1 phần → ghi nhận thời gian + % cập nhật.
  useEffect(() => {
    if (prevVisitedRef.current >= 0 && visited.size !== prevVisitedRef.current && lastTickRef.current) {
      flushProgress()
    }
    prevVisitedRef.current = visited.size
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [visited])

  // Làm tươi % tiến độ trên thanh điều hướng khi HS cuộn xem khối / trả lời (service mutate ngoài React).
  useEffect(() => {
    if (!lesson || sections.length === 0) return
    const id = window.setInterval(() => setStatsTick((t) => t + 1), 1500)
    return () => window.clearInterval(id)
  }, [lesson?.id, sections.length])

  // Chuyển phần → đặt phần hiện tại cho thống kê + ghi nhận video của phần (1 lần/phần).
  useEffect(() => {
    if (!lesson || sections.length === 0) return
    LessonStatsService.setCurrentSection(active, sections[active]?.title)
    if (sectionHasVideo[active] && !videoSeenRef.current.has(active)) {
      videoSeenRef.current.add(active)
      LessonStatsService.recordVideoWatched(`bai-giang:${lesson.slug || lesson.id}`)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [active, lesson?.id, sections.length])

  if (lesson === undefined) {
    return <div className="p-10 text-center text-gray-400">Đang tải...</div>
  }
  if (!lesson) {
    return (
      <div className="p-10 text-center">
        <p className="text-gray-500 mb-4">Không tìm thấy bài giảng.</p>
        <Link to="/teacher/elearning" className="text-blue-600 font-semibold">← Về danh sách bài giảng</Link>
      </div>
    )
  }

  const cur = sections[active]
  const curMeta = cur ? sectionKindMeta(cur) : null
  const progress = sections.length ? Math.round((visited.size / sections.length) * 100) : 0
  const statsKey = lesson ? `bai-giang:${lesson.slug || lesson.id}` : ''
  const liveStats = statsKey ? LessonStatsService.getStats(statsKey) : null

  // Tỉ lệ hoàn thành 1 phần ngay tại thời điểm hiện tại (dùng memo tổng mục → chính xác từ lần render đầu).
  const liveRatio = (i: number): number => {
    const b = (statsKey ? LessonStatsService.getStats(statsKey) : null)?.sections?.[i]
    const tT = textTotals[i] || 0, qT = gateTotals[i] || 0, vT = videoTotals[i] || 0
    const total = tT + qT + vT
    if (total === 0) return visited.has(i) ? 100 : 0
    const tD = Math.min(tT, b?.textViewed?.size || 0)
    const qD = Math.min(qT, (b?.correct || 0) + (b?.wrong || 0))
    const vD = vT > 0 && visited.has(i) ? vT : 0
    return Math.round(((tD + qD + vD) / total) * 100)
  }

  // Một mục trong thanh điều hướng các phần.
  const NavItem = ({ i }: { i: number }) => {
    const s = sections[i]
    const m = sectionKindMeta(s)
    const on = i === active
    const ratio = liveRatio(i)
    const done = ratio >= 100
    return (
      <button
        onClick={() => goto(i)}
        className={`w-full text-left rounded-xl border px-3 py-2.5 transition-all flex items-center gap-2.5 ${
          on ? 'bg-white shadow-sm border-transparent ring-2' : 'bg-transparent border-transparent hover:bg-white/70'
        }`}
        style={on ? ({ ['--tw-ring-color' as any]: m.accent } as React.CSSProperties) : undefined}
      >
        <span
          className="w-8 h-8 shrink-0 rounded-lg flex items-center justify-center text-base"
          style={{ background: `${m.accent}1a` }}
        >
          {m.icon}
        </span>
        <span className="flex-1 min-w-0">
          <span className="block text-[11px] font-bold uppercase tracking-wide" style={{ color: m.accent }}>
            {m.label}
          </span>
          <span className={`block text-sm font-semibold truncate ${on ? 'text-gray-900' : 'text-gray-600'}`}>
            {s.title}
          </span>
        </span>
        {done ? (
          <Check size={16} className="text-emerald-500 shrink-0" />
        ) : ratio > 0 ? (
          <span className="text-[10px] font-bold text-gray-400 shrink-0 tabular-nums">{ratio}%</span>
        ) : null}
      </button>
    )
  }

  return (
    <LessonDocProvider lesson={lesson}>
    <div className="min-h-screen bg-[#F8FAFC] flex flex-col">
      {/* Header */}
      <div className="bg-white border-b border-gray-100 px-4 py-3 sticky top-0 z-20">
        <div className={`${navOpen ? 'max-w-[1280px]' : 'max-w-[1600px]'} mx-auto flex items-center gap-3`}>
          <Link to="/teacher/elearning" className="text-gray-400 hover:text-gray-700">
            <ArrowLeft size={20} />
          </Link>
          {sections.length > 0 && (
            <button
              onClick={() => setNavOpen((v) => !v)}
              title={navOpen ? 'Ẩn danh sách phần' : 'Hiện danh sách phần'}
              className="p-1.5 rounded-lg text-gray-500 hover:bg-gray-100 hover:text-gray-800 transition-colors"
            >
              {navOpen ? <PanelLeftClose size={18} /> : <PanelLeftOpen size={18} />}
            </button>
          )}
          <h1 className="text-lg font-black text-gray-900 truncate">{lesson.meta.tieuDe}</h1>
          <span className="ml-auto text-xs font-bold text-gray-400 whitespace-nowrap">
            {sections.length ? `${active + 1}/${sections.length} phần` : ''}
          </span>
          <button
            onClick={toggleFullscreen}
            title={isFs ? 'Thoát toàn màn hình' : 'Toàn màn hình (giảng dạy)'}
            className="p-1.5 rounded-lg text-gray-500 hover:bg-gray-100 hover:text-gray-800 transition-colors"
          >
            {isFs ? <Minimize size={18} /> : <Maximize size={18} />}
          </button>
        </div>
        {/* Thanh tiến độ */}
        {sections.length > 0 && (
          <div className={`${navOpen ? 'max-w-[1280px]' : 'max-w-[1600px]'} mx-auto mt-2 h-1.5 rounded-full bg-gray-100 overflow-hidden`}>
            <div className="h-full bg-emerald-500 transition-all duration-500" style={{ width: `${progress}%` }} />
          </div>
        )}
      </div>

      {sections.length === 0 ? (
        <div className="p-16 text-center text-gray-400">Bài giảng chưa có nội dung.</div>
      ) : (
        <div className={`${navOpen ? 'max-w-[1280px]' : 'max-w-[1600px]'} w-full mx-auto flex-1 flex flex-col md:flex-row gap-6 p-4 md:p-6`}>
          {/* Tab điều hướng — dọc trên md+, cuộn ngang trên mobile (ẩn được để dạy toàn màn hình) */}
          {navOpen && (
            <nav className="md:w-64 md:shrink-0">
              <div className="md:sticky md:top-24 space-y-1.5 flex md:flex-col overflow-x-auto md:overflow-visible">
                {sections.map((_, i) => (
                  <div key={i} className="min-w-[200px] md:min-w-0">
                    <NavItem i={i} />
                  </div>
                ))}
              </div>
            </nav>
          )}

          {/* Khu nội dung rộng — chỉ hiện phần đang chọn */}
          <main className="flex-1 min-w-0">
            {cur && curMeta && (
              <section>
                {/* Tiêu đề phần */}
                <div className="flex items-center gap-3 mb-5">
                  <span
                    className="w-11 h-11 rounded-xl flex items-center justify-center text-xl shrink-0"
                    style={{ background: `${curMeta.accent}1a` }}
                  >
                    {curMeta.icon}
                  </span>
                  <div>
                    <span className={`inline-block text-[11px] font-black uppercase tracking-wide px-2 py-0.5 rounded-md border ${curMeta.chip}`}>
                      {curMeta.label}
                    </span>
                    <h2 className="text-xl md:text-2xl font-black text-gray-900 leading-tight">{cur.title}</h2>
                  </div>
                </div>

                {/* Lồng tiếng phần Khởi động (nếu GV đã tạo) */}
                {curMeta.kind === 'warmup' && cur.audioUrl && (
                  <div className="mb-4 rounded-xl border border-orange-100 bg-orange-50/60 p-3 flex items-center gap-3">
                    <span className="text-lg" aria-hidden>🎙️</span>
                    <span className="text-sm font-bold text-orange-800 shrink-0">Nghe dẫn nhập</span>
                    <audio controls preload="none" src={cur.audioUrl} className="flex-1 min-w-0 h-9" />
                  </div>
                )}

                {/* Các thẻ trong phần — phần "Hình thành kiến thức" hiển thị dạng lộ trình vượt ải */}
                {curMeta.kind === 'content' && cur.cards.length > 0 ? (
                  <QuestSection
                    cards={cur.cards}
                    statsKey={statsKey}
                    sectionIndex={active}
                    sectionTitle={cur.title}
                    lessonSlug={lesson.slug || lesson.id}
                    accent={curMeta.accent}
                    bypassLock={bypassLock}
                  />
                ) : (
                  <div className="space-y-4">
                    {cur.cards.map((card) => (
                      <div key={card.id} className="bg-white rounded-xl border border-gray-100 shadow-sm p-5 md:p-6">
                        <LessonCardRenderer card={card} />
                      </div>
                    ))}
                  </div>
                )}

                {/* Điều hướng Phần trước / sau */}
                <div className="flex items-center justify-between gap-3 mt-8">
                  <button
                    onClick={() => goto(active - 1)}
                    disabled={active === 0}
                    className="flex items-center gap-1.5 px-4 py-2.5 rounded-xl text-sm font-bold bg-white border border-gray-200 text-gray-600 hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed"
                  >
                    <ChevronLeft size={18} /> Phần trước
                  </button>
                  {active < sections.length - 1 ? (
                    <button
                      onClick={() => goto(active + 1)}
                      className="flex items-center gap-1.5 px-5 py-2.5 rounded-xl text-sm font-black text-white shadow-md hover:brightness-110"
                      style={{ background: curMeta.accent }}
                    >
                      Phần tiếp theo <ChevronRight size={18} />
                    </button>
                  ) : (
                    <span className="flex items-center gap-1.5 px-5 py-2.5 rounded-xl text-sm font-black bg-emerald-50 text-emerald-700 border border-emerald-200">
                      <Check size={18} /> Hoàn thành bài giảng
                    </span>
                  )}
                </div>

                {/* Tổng kết học tập (phần cuối) */}
                {active === sections.length - 1 && (
                  <div className="mt-6 rounded-2xl border border-emerald-200 bg-emerald-50 p-5">
                    <h3 className="text-base font-black text-emerald-800 mb-3 flex items-center gap-2">
                      <Check size={18} /> Tổng kết học tập
                    </h3>
                    <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                      {[
                        { n: `${progress}%`, t: 'Nội dung đã xem' },
                        { n: `${visited.size}/${sections.length}`, t: 'Phần đã xem' },
                        { n: liveStats ? `${liveStats.correctAnswers}/${liveStats.correctAnswers + liveStats.wrongAnswers}` : '0/0', t: 'Câu trả lời đúng' },
                        { n: liveStats?.aiQuestionsAsked ?? 0, t: 'Lần hỏi AI' },
                      ].map((s, i) => (
                        <div key={i} className="bg-white rounded-xl border border-emerald-100 p-3 text-center">
                          <div className="text-xl font-black text-emerald-700">{s.n}</div>
                          <div className="text-[11px] text-gray-500 mt-0.5">{s.t}</div>
                        </div>
                      ))}
                    </div>
                    <p className="text-[11px] text-gray-500 mt-2">Kết quả được lưu về giáo viên khi bạn rời bài học.</p>
                  </div>
                )}
              </section>
            )}
          </main>
        </div>
      )}
    </div>
    </LessonDocProvider>
  )
}

export default LessonViewerPage
